// SPEC-LF1-STORE-LOCAL-CACHE-01: 巡回 record の IndexedDB CRUD + synced フラグ管理。
// ARCH R1: 1 booth = 1 transaction (atomic、auto-rollback on crash) を putRecord で保証。
// ARCH R4: LF1 では削除しない、synced flag のみ更新 (lifecycle は LF2)。

import { getDb, STORE_PATROL_RECORDS, STORE_STORE_META } from './db'
import { logger } from '../logger'

const LOG_ERR_WRITE   = 'ERR-LF1-IDB-WRITE'
const LOG_ERR_READ    = 'ERR-LF1-IDB-READ'
const LOG_ERR_QUOTA   = 'ERR-LF1-IDB-QUOTA'

function syncedKey(b) { return b ? 'true' : 'false' }

// DIAG-PREV-STALE-BASELINE-01 (D-233) MEASURE_5: 「前回値に古い値が出る」件の A/B 切り分け用に、
//   同期成功と baseline 書き込みの直近時刻をメモリ上に保持する(記録のみ。挙動・保存・同期は変えない)。
//   絶対時刻は送らず、読み手側で経過ミリ秒に変換して Sentry へ出す。
let _d233LastSyncSuccessAt = null
let _d233LastBaselineWriteAt = null
export function markSyncSuccessNow() { _d233LastSyncSuccessAt = Date.now() }
export function markBaselineWriteNow() { _d233LastBaselineWriteAt = Date.now() }
export function msSinceLastSyncSuccess() { return _d233LastSyncSuccessAt == null ? null : Date.now() - _d233LastSyncSuccessAt }
export function msSinceLastBaselineWrite() { return _d233LastBaselineWriteAt == null ? null : Date.now() - _d233LastBaselineWriteAt }
export function _resetD233Timestamps() { _d233LastSyncSuccessAt = null; _d233LastBaselineWriteAt = null }

// JST 日付 (yyyy-mm-dd)。toISOString() は UTC ずれで前/翌日事故を起こすため sv-SE + Asia/Tokyo。
function todayJST() {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Tokyo' })
}

/**
 * 1 booth 1 record の atomic put。
 * record shape (LF1 で取り扱う最小 subset):
 *  - localId       (uuid, IDB key)
 *  - booth_code    (text)
 *  - store_code    (text)
 *  - patrol_date   (text YYYY-MM-DD)
 *  - in_meter / in_meter_2 / in_meter_3 / in_meter_4 / out_meter / out_meter_2 / out_meter_3 (number|null)  // D-156: IN2-4 は ...optionalPatch 経由で自動収録
 *  - prize_stock_count / prize_restock_count (number|null)
 *  - entry_type    ('patrol'|'replace'|'collection')
 *  - source        ('manual'|'ocr'|...)
 *  - input_method  ('manual'|'ocr')
 *  - prize_name / prize_cost / set_* (任意)
 *  - synced        (boolean)
 *  - syncedKey     (syncedKey(synced))
 *  - createdLocally(ISO timestamp)
 *
 * 失敗時は ERR-LF1-IDB-WRITE で logger + 例外 throw (上位がユーザー警告を出す)。
 */
export async function putPatrolRecord(record) {
  try {
    const db = await getDb()
    const now = new Date().toISOString()
    const localId = record.localId ?? (crypto.randomUUID?.() ?? `lf-${Date.now()}-${Math.random().toString(36).slice(2)}`)
    const synced = record.synced ?? false
    const merged = {
      ...record,
      localId,
      synced,
      syncedKey: syncedKey(synced),
      createdLocally: record.createdLocally ?? now,
      updatedLocally: now,
    }
    const tx = db.transaction(STORE_PATROL_RECORDS, 'readwrite')
    await tx.store.put(merged)
    await tx.done
    return merged
  } catch (err) {
    const tag = err?.name === 'QuotaExceededError' ? LOG_ERR_QUOTA : LOG_ERR_WRITE
    logger.error?.(tag, { boothCode: record?.booth_code, message: err?.message })
    throw new IdbWriteError(tag, err)
  }
}

export class IdbWriteError extends Error {
  constructor(code, cause) {
    super(cause?.message ?? code)
    this.code = code
    this.cause = cause
  }
}

export async function getPatrolRecordsByStore(storeCode) {
  if (!storeCode) return []
  try {
    const db = await getDb()
    return await db.getAllFromIndex(STORE_PATROL_RECORDS, 'byStoreCode', storeCode)
  } catch (err) {
    logger.error?.(LOG_ERR_READ, { storeCode, message: err?.message })
    return []
  }
}

export async function getPatrolRecordsByBooth(boothCode) {
  if (!boothCode) return []
  try {
    const db = await getDb()
    return await db.getAllFromIndex(STORE_PATROL_RECORDS, 'byBoothCode', boothCode)
  } catch (err) {
    logger.error?.(LOG_ERR_READ, { boothCode, message: err?.message })
    return []
  }
}

export async function getUnsyncedRecords() {
  try {
    const db = await getDb()
    return await db.getAllFromIndex(STORE_PATROL_RECORDS, 'bySynced', syncedKey(false))
  } catch (err) {
    logger.error?.(LOG_ERR_READ, { message: err?.message })
    return []
  }
}

/**
 * SPEC-LF1-HISTORY-FIX-01: server baseline (synced=true) rows を IDB に書き込む。
 * idempotent: localId = reading_id を採用するため再 fetch しても同じ row は overwrite される。
 * D-1099: 同じ localId の既存の行が synced === false(未送信のローカル編集)なら put しない。
 *   ★以前は無条件に put して未送信の行を synced:true のサーバ行で上書きしていた(コメントは「触れない」だった)。
 *   残した行を送信済みにするかは、すぐ後の reconcileSyncedByBaseline(D-1098: reading_id 一致 + 送る中身の全列一致)
 *   だけが決める。synced の行・存在しない行は今まで通り put する(baseline の更新・タブが落ちた後の回復)。
 *   残したときは LF1_BASELINE_KEPT_UNSYNCED を warn で出す(localId だけ。値は出さない)。
 *
 * 1 store の全 rows を 1 トランザクションで処理 (ARCH R1 transaction 原則の延長)。
 * @returns {Promise<number>} put した件数(残した未送信の行は数えない)
 */
export async function putBaselineRows(rows) {
  if (!rows || !rows.length) return 0
  // SPEC-LF1-HISTORY-FIX-03 C3 guard: index key 必須フィールド (store_code) が欠落した row を
  // 検知してログ。IDB index byStoreCode で entry が作られず、getPatrolRecordsByStore が
  // silent に 0 件返却する (= 全列 '−' バグ) のを未然に検知する。
  // DIAG-LF1-HISTORY-RUNTIME-01 で実際に発生した症状の再発防止策。
  const missingStoreCode = rows.filter(r => r && r.store_code == null).length
  if (missingStoreCode > 0) {
    logger.warn?.('ERR-LF1-IDB-INDEX-MISSING', {
      phase: 'baseline',
      missingStoreCode,
      total: rows.length,
      hint: 'HISTORY_SELECT に store_code が含まれていない可能性、IDB byStoreCode index 落ちで history 全列 - になる',
    })
  }
  try {
    const db = await getDb()
    const tx = db.transaction(STORE_PATROL_RECORDS, 'readwrite')
    const nowIso = new Date().toISOString()
    let count = 0
    const keptUnsynced = []
    for (const row of rows) {
      const localId = row.reading_id ?? `baseline-${row.booth_code}-${row.patrol_date}`
      // D-1099: 判定は同じトランザクションの中で get してから行う(get と put の間に他の書き込みが入らない)。
      const existing = await tx.store.get(localId)
      if (existing && existing.synced === false) {
        keptUnsynced.push(localId)
        continue
      }
      await tx.store.put({
        ...row,
        localId,
        synced: true,
        syncedKey: syncedKey(true),
        syncedAt: nowIso,
        baselineRefreshedAt: nowIso,
      })
      count++
    }
    await tx.done
    markBaselineWriteNow() // D-233 MEASURE_5: baseline 書き込み時刻を記録(削除条件・書込ロジックは不変)
    // D-1099: ログは tx の外で出す(IDB 以外の処理を tx の途中に挟まない)。
    if (keptUnsynced.length > 0) {
      logger.warn?.('LF1_BASELINE_KEPT_UNSYNCED', { localIds: keptUnsynced })
    }
    return count
  } catch (err) {
    const tag = err?.name === 'QuotaExceededError' ? LOG_ERR_QUOTA : LOG_ERR_WRITE
    logger.error?.(tag, { phase: 'baseline', count: rows.length, message: err?.message })
    throw new IdbWriteError(tag, err)
  }
}

export async function markRecordSynced(localId) {
  if (!localId) return null
  try {
    const db = await getDb()
    const tx = db.transaction(STORE_PATROL_RECORDS, 'readwrite')
    const existing = await tx.store.get(localId)
    if (!existing) { await tx.done; return null }
    const next = {
      ...existing,
      synced: true,
      syncedKey: syncedKey(true),
      syncedAt: new Date().toISOString(),
    }
    await tx.store.put(next)
    await tx.done
    return next
  } catch (err) {
    logger.error?.(LOG_ERR_WRITE, { localId, message: err?.message })
    return null
  }
}

// SPEC-LOCAL-RECORD-REKEY-TO-READING-ID-01 (D-234): 同期成功時にローカル行のキー(localId=新規UUID)を
//   サーバの reading_id へ付け替える。これでローカル行とサーバ baseline 行が同一 ID になり、
//   sweepBaselineOrphans の削除対象から外れ、履歴の二重行が解消する。put と delete は同一トランザクションで
//   行い、途中で行が消える窓を作らない。既に readingId キーに baseline 行があれば上書き(同一レコードであり
//   ローカル側が最新値を持つため意図した挙動)。IDB スキーマ・keyPath は変えない(localId in-line key のまま)。
// SPEC-LF1-REKEY-MERGE-FIX-01 (D-808) AC3: 第3引数 savedOptional = その同期でサーバに確定した optional 列
//   (patrolCore.savePatrolReading の戻り値)。非 null ならトップレベルへマージし、ローカル行をサーバ行と同じ形にする。
//   ★従来は前回値から引き継いだ景品/設定が defaultsFromPrev の中にしか無く、buildPrevFromRows(最上位だけを見る)
//     が次回の前回値から景品キーを落としていた(D-807 candidate_1)。
//   ★INSERT 経路は reading_id = localId(冪等キー D2)なのでキーは替わらない。その場合も savedOptional があれば
//     同じキーのまま中身だけ書き直す(従来はここで何もせず return していた = 直らない経路)。
//   ★savedOptional が null(duplicate / stale)なら従来と 1 バイトも変えない。
export async function rekeyPatrolRecordToReadingId(localId, readingId, savedOptional = null) {
  if (!localId) return false
  if (readingId == null) return false
  const sameKey = readingId === localId
  if (sameKey && savedOptional == null) return false // 一致 かつ 書き直す中身なし = 何もしない(従来どおり)
  try {
    const db = await getDb()
    const tx = db.transaction(STORE_PATROL_RECORDS, 'readwrite')
    const existing = await tx.store.get(localId)
    if (!existing) { await tx.done; return false }
    const next = {
      ...existing,            // read_time / patrol_date / メーター / optionalPatch / defaultsFromPrev 等を保持
      ...(savedOptional ?? {}), // D-808: サーバに確定した景品/設定をトップレベルへ
      localId: readingId,     // 新キー = サーバの reading_id
      reading_id: readingId,  // reading_id フィールドも揃える
      synced: true,
      syncedKey: syncedKey(true),
    }
    await tx.store.put(next)        // 新キーへ(既存 baseline があれば上書き)
    if (!sameKey) await tx.store.delete(localId)  // 旧キー削除(同一 tx = 消失窓なし)。同じキーなら put が上書き済み
    await tx.done
    return true
  } catch (err) {
    logger.error?.(LOG_ERR_WRITE, { localId, readingId, message: err?.message })
    return false
  }
}

// SPEC-LF1-IDEMPOTENT-SYNC-01 D5: 送信失敗時のみ lastErrCode を IDB record に残す。
// server 側で不可視な stuck record を D7 バナー詳細で端末側から診断するため。synced は変えない。
export async function setRecordLastErrCode(localId, errCode) {
  if (!localId) return null
  try {
    const db = await getDb()
    const tx = db.transaction(STORE_PATROL_RECORDS, 'readwrite')
    const existing = await tx.store.get(localId)
    if (!existing) { await tx.done; return null }
    const next = { ...existing, lastErrCode: errCode ?? null, lastErrAt: new Date().toISOString() }
    await tx.store.put(next)
    await tx.done
    return next
  } catch (err) {
    logger.error?.(LOG_ERR_WRITE, { localId, message: err?.message })
    return null
  }
}

// 未送信件数 + 関与 store 数 を集計してバナーに渡す。
export async function getUnsyncedSummary() {
  const unsynced = await getUnsyncedRecords()
  const storeSet = new Set()
  for (const r of unsynced) if (r.store_code) storeSet.add(r.store_code)
  return { count: unsynced.length, storeCount: storeSet.size, records: unsynced }
}

// D-982c (gate_3 差し戻し是正): 更新通知の未送信ガード専用、厳格(strict)な件数読み取り。
//   ★なぜ要るか: getUnsyncedRecords/getUnsyncedSummary は IDB 例外を catch して [] / count:0 を返す
//   (= fail-open)。更新通知側がこれを呼ぶと「読み取り失敗」と「本当に未送信ゼロ」を区別できず、
//   確認なしで更新が適用されてしまう(gate_3 (a) 指摘)。
//   ★既存関数(getUnsyncedRecords/getUnsyncedSummary)の実装・戻り値・catch は 1 バイトも変えていない。
//   既存の呼び出し元(useUnsentBanner 等)への影響はゼロ。
//   ★この関数は読み取りのみ(getDb + getAllFromIndex)。書き込み・削除・Sentry 送信は一切しない。
//   catch を持たないため、IDB 障害時は呼び出し元まで例外がそのまま伝播する(意図的な fail-closed)。
export async function countUnsyncedRecordsStrict() {
  const db = await getDb()
  const unsynced = await db.getAllFromIndex(STORE_PATROL_RECORDS, 'bySynced', syncedKey(false))
  const storeSet = new Set()
  for (const r of unsynced) if (r.store_code) storeSet.add(r.store_code)
  return { count: unsynced.length, storeCount: storeSet.size }
}

/**
 * FIX4: store_code:null の未送信レコードを掃除。
 * - booth_code から逆引き可能なら store_code を補完して残す。
 * - 逆引き不能 (= 真の孤児) なら IDB から削除。
 * useUnsentBanner 初期化時に1回実行することで既存幽霊レコードを自動消滅させる。
 */
function _deriveStoreCode(boothCd) {
  if (!boothCd) return null
  if (/^[A-Z]{3}\d{2}::/.test(boothCd)) return boothCd.split('::')[0]
  const p = boothCd.split('-')
  return p.length >= 2 ? p[0] : null
}

export async function deleteOrphanedNullStoreRecords() {
  try {
    const db = await getDb()
    const all = await db.getAllFromIndex(STORE_PATROL_RECORDS, 'bySynced', syncedKey(false))
    const nullStoreRecords = all.filter(r => r.store_code == null)
    if (!nullStoreRecords.length) return 0
    const tx = db.transaction(STORE_PATROL_RECORDS, 'readwrite')
    let deleted = 0
    let patched = 0
    for (const r of nullStoreRecords) {
      const derived = _deriveStoreCode(r.booth_code)
      if (derived) {
        await tx.store.put({ ...r, store_code: derived })
        patched++
      } else {
        await tx.store.delete(r.localId)
        deleted++
      }
    }
    await tx.done
    if (deleted > 0 || patched > 0) {
      logger.warn?.('ERR-LF1-ORPHAN-SWEEP', { deleted, patched })
    }
    return deleted
  } catch (err) {
    logger.error?.(LOG_ERR_WRITE, { phase: 'orphan_sweep', message: err?.message })
    return 0
  }
}

// ─────────────────────────────────────────────────────────
// D-1098: 「送信済み」の判定は、送るときの中身がサーバ行と全部同じかで決める。
//   ★値が似ているかで消し込むと、比べていない列の書き込みを必ず失う(中洲 B02: set_c だけ直した入替 4 件が
//     4 項目一致で synced にされ、sweep で消えた)。迷ったら送る側に倒す(再送は冪等 = 23505 / stale / unchanged は ok)。
// ─────────────────────────────────────────────────────────

// patrolCore.js の OPTIONAL_DEFAULT_KEYS(INSERT 時に前回値から補完する列)と同じ並び。
//   ★patrolCore は getStoreMeta をこのファイルから import しているので、ここから patrolCore を import すると
//     循環になる(depcruise no-circular)。写しを持ち、ずれは reconcileSettingsOnlyD1098.test.js が検知する。
export const UPLOAD_OPTIONAL_DEFAULT_KEYS = [
  'prize_id', 'prize_id_2', 'prize_id_3',
  'prize_name', 'prize_name_2', 'prize_name_3',
  'set_a', 'set_c', 'set_l', 'set_r',
  'prize_cost', 'prize_cost_1', 'prize_cost_2', 'prize_cost_3',
]

function toFloatOrNull(v) {
  if (v === '' || v == null) return null
  const n = parseFloat(v)
  return Number.isNaN(n) ? null : n
}
function toIntOrNull(v) {
  if (v === '' || v == null) return null
  const n = parseInt(v, 10)
  return Number.isFinite(n) ? n : null
}

/**
 * D-1098: ローカル行を storeSync → savePatrolReading で送るとき、payload に入る「人の入力から決まる列」を作る。
 *   patrolCore.js savePatrolReading の INSERT payload(basePayload + 前回値の補完 + optionalPatch、normalizeSlotStock)
 *   と同じ変換をする。★比べないのは送信時にその場で決まる列・サーバ側の列だけ:
 *   read_time(送信時刻)/ play_price(送信時に店舗キャッシュから解決)/ created_by / organization_id /
 *   booth_id / full_booth_code / store_code / machine_code(booth_code から決まる)/ reading_id(別に照合する)/
 *   基本の source・input_method(定数 'manual'。OCR のときは optionalPatch に入るので比べる)。
 *   巡回の UPDATE の道は optionalPatch しか送らないが、ここでは前回値の補完も含めた広い方で比べる(一致しにくい = 送る側)。
 * @param {object} local IDB の patrol record
 * @returns {Record<string, unknown>}
 */
export function buildUploadComparable(local) {
  const out = {
    booth_code: local?.booth_code ?? null,
    patrol_date: local?.patrol_date ?? null,
    entry_type: local?.entry_type ?? 'patrol',
    in_meter: toFloatOrNull(local?.in_meter),
    out_meter: toFloatOrNull(local?.out_meter),
    // savePatrolReading: 在庫の空欄は 0 で送る / 補充の空欄は null(D-608)
    prize_stock_count: toIntOrNull(local?.prize_stock_count) ?? 0,
    prize_restock_count: toIntOrNull(local?.prize_restock_count),
  }
  const prev = local?.defaultsFromPrev
  if (prev && typeof prev === 'object') {
    for (const k of UPLOAD_OPTIONAL_DEFAULT_KEYS) {
      if (Object.prototype.hasOwnProperty.call(prev, k) && prev[k] !== undefined) out[k] = prev[k]
    }
  }
  const patch = local?.optionalPatch
  if (patch && typeof patch === 'object') {
    for (const [k, v] of Object.entries(patch)) out[k] = (v === '' || v === undefined) ? null : v
  }
  // normalizeSlotStock: out_meter_N があって stock_N が無ければ 0 で送る
  if (out.out_meter_2 != null && out.stock_2 == null) out.stock_2 = 0
  if (out.out_meter_3 != null && out.stock_3 == null) out.stock_3 = 0
  return out
}

function stableStringify(v) {
  if (Array.isArray(v)) return `[${v.map(stableStringify).join(',')}]`
  if (v && typeof v === 'object') {
    return `{${Object.keys(v).sort().map(k => `${JSON.stringify(k)}:${stableStringify(v[k])}`).join(',')}}`
  }
  return JSON.stringify(v)
}

function isNumericString(s) {
  return typeof s === 'string' && s.trim() !== '' && Number.isFinite(Number(s))
}

/**
 * D-1098 DESIGN 2/3: 1 列ぶんの比較(型の扱いはここ 1 か所)。
 *   - サーバ行にその列が無い(undefined)→ 一致しない(比べられない列があれば送る)
 *   - null と '' は同じ「空」。空と 0 は違う
 *   - 数値と数の文字列は数として比べる('13' と 13 は同じ)。それ以外の文字列は完全一致
 *   - オブジェクト・配列(set_ext / meter_exchanges)はキー順に依らない中身で比べる
 * @param {unknown} localValue 送る値
 * @param {unknown} serverValue サーバ行の値
 */
export function sameUploadValue(localValue, serverValue) {
  if (serverValue === undefined) return false
  const lEmpty = localValue == null || localValue === ''
  const sEmpty = serverValue == null || serverValue === ''
  if (lEmpty || sEmpty) return lEmpty && sEmpty
  if (typeof localValue === 'number' || typeof serverValue === 'number') {
    const ln = typeof localValue === 'number' ? localValue : (isNumericString(localValue) ? Number(localValue) : NaN)
    const sn = typeof serverValue === 'number' ? serverValue : (isNumericString(serverValue) ? Number(serverValue) : NaN)
    return Number.isFinite(ln) && Number.isFinite(sn) && ln === sn
  }
  if (typeof localValue === 'object' || typeof serverValue === 'object') {
    return typeof localValue === 'object' && typeof serverValue === 'object' &&
      stableStringify(localValue) === stableStringify(serverValue)
  }
  return localValue === serverValue
}

// 一致しない列の名前(値は返さない。ログに現場の値を出さないため)。
function uploadDiffKeys(local, serverRow) {
  const comparable = buildUploadComparable(local)
  return Object.keys(comparable).filter(k => !sameUploadValue(comparable[k], serverRow?.[k]))
}

/**
 * SPEC-LF1-UNSENT-RECONCILE-FIX-01 FIX_A:
 * iOS Safari タブkillで markRecordSynced が未実行になったレコードを reconcile。
 * refreshBaselineAndRender 後に呼ぶことで、次回ページロード時に自動回復する。
 *
 * D-1098: synced にしてよいのは次の 2 つが両方そろったときだけ。
 *   1. サーバ行の reading_id がローカル行の localId と同じ(送信は reading_id = localId で INSERT する
 *      / patrolCore.js:479・609、storeSync.js:156)。別の入替・別の保存の行とは照合しない。
 *   2. そのローカル行を送るときの中身(buildUploadComparable の全列)がサーバ行と全部同じ。
 *      ★ID だけでは一致させない(巡回の書き直しで localId が既にサーバの reading_id の行の編集を飲み込む)。
 *   どちらかが欠けたら unsynced のまま = 次の送信で送る(冪等なので害は無い)。
 */
export async function reconcileSyncedByBaseline(baselineRows) {
  if (!baselineRows || !baselineRows.length) return 0
  const unsynced = await getUnsyncedRecords()
  if (!unsynced.length) return 0

  const byReadingId = new Map()
  for (const row of baselineRows) {
    if (row && row.reading_id != null) byReadingId.set(String(row.reading_id), row)
  }

  let reconciled = 0
  for (const local of unsynced) {
    if (local?.localId == null) continue
    const serverRow = byReadingId.get(String(local.localId))
    if (!serverRow) continue
    const diffKeys = uploadDiffKeys(local, serverRow)
    const ctx = {
      boothCode: local.booth_code, localId: local.localId,
      patrol_date: local.patrol_date ?? null, entry_type: local.entry_type ?? 'patrol',
    }
    if (diffKeys.length > 0) {
      logger.warn?.('LF1_RECONCILE_KEPT_UNSYNCED', { ...ctx, diffKeys })
      continue
    }
    const result = await markRecordSynced(local.localId)
    if (result) {
      reconciled++
      logger.warn?.('LF1_RECONCILE_SYNCED', ctx)
    }
  }
  if (reconciled > 0) logger.warn?.('LF1_RECONCILE_DONE', { reconciled })
  return reconciled
}

// putBaselineRows と同一の localId 採番 (localId = reading_id、無ければ合成キー)。
// sweep が「生き残すべき baseline 行の localId」を正確に判定するため必ず一致させる。
function baselineLocalId(row) {
  return row.reading_id ?? `baseline-${row.booth_code}-${row.patrol_date}`
}

/**
 * SPEC-LF1-BASELINE-AUTHORITATIVE-SWEEP-01: server baseline を「その booth のカバー日付
 * 範囲内では権威」とみなし、baseline に含まれない synced=true の IDB 幽霊行を掃除する。
 * - server で削除された行 (admin_delete_meter_reading) の端末側残留を消す
 * - localId != reading_id の legacy 重複 (idempotent upload 以前) を消す。生き残るのは
 *   putBaselineRows が localId=reading_id で書いた baseline 行そのもの。
 *
 * 安全不変条件:
 *   INV1 synced=false 行は絶対に削除しない (step3 で無条件除外)
 *   INV2 空/失敗 baseline では 0 削除
 *   INV3 カバー範囲 [minDate(baseline), today(JST)] 外の行は削除しない
 *        (下限=baseline最小日付。上限は today まで拡張: D-040 — baseline fetch は最新N件の
 *         desc pull なので、baseline最大日付より新しく today以下の synced 行は server 削除済み
 *         幽霊と確定できる。today超の未来日 (clock skew) は触らない)
 *   INV4 最悪ケースは「まだ server に在る synced コピーの削除」= 次の baseline fetch で復元。
 *        表示のみ影響でデータ喪失ゼロ。安全>確実>快適 の順序を構造的に保持。
 *
 * baselineRows は store 全体を渡しても良い (boothCode で内部的に絞り込み、window を
 * 他 booth の日付で広げない = INV3 を跨ぎから守る)。
 *
 * @param {Array} baselineRows  server baseline rows
 * @param {{boothCode: string}} opts
 * @returns {Promise<number>} 削除件数
 */
export async function sweepBaselineOrphans(baselineRows, { boothCode } = {}) {
  // INV2 + guard: 空/null baseline、boothCode 不在なら何もしない。
  if (!baselineRows || !baselineRows.length || !boothCode) return 0
  const boothBaseline = baselineRows.filter(r => r && r.booth_code === boothCode)
  if (!boothBaseline.length) return 0

  // 下限 = この booth の baseline 最小日付。「生かす localId」set も同時に構築。
  let minDate = null
  const keepIds = new Set()
  for (const r of boothBaseline) {
    keepIds.add(baselineLocalId(r))
    const d = r.patrol_date
    if (d == null) continue
    if (minDate == null || d < minDate) minDate = d
  }
  if (minDate == null) return 0
  // D-040: 上限は baseline 最大日付ではなく today(JST)。baseline より新しく today以下の
  // synced 行 = server 削除済み幽霊 (最新N件 desc fetch に載るはずの行が不在 → 削除された)。
  const today = todayJST()

  try {
    const rows = await getPatrolRecordsByBooth(boothCode)
    // step3: synced===true かつ window 内のみ対象。synced=false は無条件除外 (INV1)。
    // step5: window 外 (patrol_date < minDate or > today) は対象外 (INV3)。today超の未来日も除外。
    const orphans = rows.filter(r =>
      r.synced === true &&
      r.patrol_date != null &&
      r.patrol_date >= minDate &&
      r.patrol_date <= today &&
      !keepIds.has(r.localId)
    )
    if (!orphans.length) return 0
    const db = await getDb()
    const tx = db.transaction(STORE_PATROL_RECORDS, 'readwrite')
    let deleted = 0
    for (const r of orphans) {
      await tx.store.delete(r.localId)
      deleted++
    }
    await tx.done
    // D-1098: 消した行は本番でも残す(info は本番に出ない)。値は出さず、ID・日付・種別だけ。
    if (deleted > 0) {
      logger.warn?.('LF1_BASELINE_SWEEP', {
        boothCode, deleted,
        localIds: orphans.map(r => r.localId),
        rows: orphans.map(r => ({ localId: r.localId, patrol_date: r.patrol_date, entry_type: r.entry_type ?? null })),
      })
    }
    return deleted
  } catch (err) {
    // reconciliation は正常系。Sentry error にせず継続 (呼び出し側で描画を止めない)。
    logger.error?.(LOG_ERR_WRITE, { phase: 'baseline_sweep', boothCode, message: err?.message })
    return 0
  }
}

// store メタ (storeName / machines / prefetchedAt) を保存。prefetch / load 共通。
export async function putStoreMeta(storeCode, meta) {
  if (!storeCode) return
  try {
    const db = await getDb()
    const tx = db.transaction(STORE_STORE_META, 'readwrite')
    await tx.store.put({ store_code: storeCode, ...meta, updatedAt: new Date().toISOString() })
    await tx.done
  } catch (err) {
    logger.error?.(LOG_ERR_WRITE, { storeCode, message: err?.message })
  }
}

export async function getStoreMeta(storeCode) {
  if (!storeCode) return null
  try {
    const db = await getDb()
    return await db.get(STORE_STORE_META, storeCode)
  } catch (err) {
    logger.error?.(LOG_ERR_READ, { storeCode, message: err?.message })
    return null
  }
}
